import type { TableColumnType, TablePaginationConfig } from "antd";
import { isValidElement, type Key, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

type SortOrder = Exclude<TableColumnType<unknown>["sortOrder"], undefined>;
import { z } from "zod";
import { SortDirection } from "../gql/generated/graphqlSchema.tsx";

interface SorterInfo {
  order?: SortOrder;
  columnKey?: Key;
}

const sortDirectionValues = [SortDirection.Asc, SortDirection.Desc] as const;

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

/**
 * Builds a URL query string from a params object, omitting entries with `undefined` values.
 *
 * @example
 * buildSearchUrl({ q: "cake", page: undefined, sortField: "NAME" })
 * // => "?q=cake&sortField=NAME"
 *
 * buildSearchUrl({ page: undefined })
 * // => ""
 */
export const buildSearchUrl = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, value);
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Creates a Zod schema for table-related URL search params: `page`, `sortField`, and `sortDirection`.
 * Extend the result with page-specific params via `.extend(...)`.
 *
 * @param columnToSortField - Mapping of Ant Design column keys to GraphQL sort field enum values.
 *   The enum values are used to validate the `sortField` search param.
 *
 * @example
 * const columnToSortField = {
 *   name: RecipeSortField.Name,
 *   createdAt: RecipeSortField.CreatedAt,
 * };
 *
 * const searchParamsSchema = tableSearchParams(columnToSortField).extend({
 *   q: z.string().optional(),
 * });
 * // Parses: { page: number, sortField?: RecipeSortField, sortDirection?: SortDirection, q?: string }
 */
export const tableSearchParams = <const SF extends string>(columnToSortField: Record<string, SF>) =>
  z.object({
    page: z.coerce.number().int().positive().optional().default(1).catch(1),
    pageSize: z.coerce.number().int().positive().optional().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
    sortField: z.enum(Object.values(columnToSortField)).optional().catch(undefined),
    sortDirection: z.enum(sortDirectionValues).optional().catch(undefined),
  });

interface UseTableParamsOptions<SortField extends string, ColumnKey extends string> {
  pageSize: number;
  page: number;
  sortField?: SortField;
  sortDirection?: SortDirection;
  columnToSortField: Record<ColumnKey, SortField>;
  /** Enables the Ant Design `showSizeChanger` with the given options. */
  pageSizeOptions?: number[];
  /** Page size omitted from the URL when equal to this value. Required if `pageSizeOptions` is set. */
  defaultPageSize?: number;
}

/**
 * Hook for managing Ant Design Table sorting and pagination via URL search params.
 *
 * Encapsulates the common CRUD list page pattern: parsing sort/pagination state from the URL,
 * computing GraphQL query variables, and navigating on table interactions.
 *
 * Both generic type parameters (`SortField`, `ColumnKey`) are inferred from `columnToSortField` -
 * no manual type annotations needed.
 *
 * @returns
 * - `offset` / `limit` - Computed from `page` and `pageSize`, ready for GraphQL variables.
 * - `sortOrderFor(columnKey)` - Returns the Ant Design `SortOrder` for a column.
 *   `columnKey` is type-safe: only keys present in `columnToSortField` are accepted.
 * - `paginationConfig(total, extraParams?)` - Returns a ready-made Ant Design `TablePaginationConfig` with `itemRender` wrapping each page button in a `<Link>`.
 * - `createTableChangeHandler(extraParams?)` - Returns an `onChange` handler for the Table.
 *   Pass any extra search params (e.g. `{ q: search }`) that should be preserved in the URL.
 *
 * @example
 * // 1. Define the column-to-sort-field mapping (module level)
 * const columnToSortField = {
 *   name: RecipeSortField.Name,
 *   createdAt: RecipeSortField.CreatedAt,
 *   updatedAt: RecipeSortField.UpdatedAt,
 * };
 *
 * // 2. Create the search params schema (module level)
 * const searchParamsSchema = tableSearchParams(columnToSortField).extend({
 *   q: z.string().optional(),
 * });
 *
 * // 3. Use in component
 * const { q: search, page, sortField, sortDirection } = useSafeSearchParams(searchParamsSchema);
 *
 * const table = useTableParams({
 *   pageSize: 20,
 *   page,
 *   sortField,
 *   sortDirection,
 *   columnToSortField,
 * });
 *
 * // GraphQL query variables
 * useQuery(GetRecipesDocument, {
 *   variables: {
 *     search,
 *     pagination: { offset: table.offset, limit: table.limit, sortField, sortDirection },
 *   },
 * });
 *
 * // Ant Design Table props
 * <Table
 *   columns={[
 *     {
 *       key: "name",
 *       sorter: true,
 *       sortDirections: tableSortDirections,
 *       sortOrder: table.sortOrderFor("name"),  // type-safe: only "name" | "createdAt" | "updatedAt"
 *     },
 *   ]}
 *   onChange={table.createTableChangeHandler({ q: search })}
 *   pagination={table.paginationConfig(total, { q: search })}
 * />
 */
export const useTableParams = <SortField extends string, ColumnKey extends string>({
  pageSize,
  page,
  sortField,
  sortDirection,
  columnToSortField,
  pageSizeOptions,
  defaultPageSize,
}: UseTableParamsOptions<SortField, ColumnKey>) => {
  const navigate = useNavigate();

  const sortFieldToColumn: Record<string, string> = {};
  const columnToSortFieldLookup: Record<string, string> = {};
  for (const [column, field] of Object.entries<string>(columnToSortField)) {
    sortFieldToColumn[field] = column;
    columnToSortFieldLookup[column] = field;
  }

  const sortOrderFor = (columnKey: ColumnKey): SortOrder => {
    if (!sortField || sortFieldToColumn[sortField] !== columnKey) return null;
    return sortDirection === SortDirection.Asc ? "ascend" : "descend";
  };

  const pageSizeParam = (size: number): string | undefined =>
    defaultPageSize !== undefined && size === defaultPageSize ? undefined : String(size);

  const buildUrl = (
    targetPage: number,
    targetPageSize: number,
    targetSortField?: string,
    targetSortDirection?: string,
    extraParams?: Record<string, string | undefined>,
  ) =>
    buildSearchUrl({
      ...extraParams,
      page: targetPage === 1 ? undefined : String(targetPage),
      pageSize: pageSizeParam(targetPageSize),
      sortField: targetSortField,
      sortDirection: targetSortDirection,
    });

  const paginationConfig = (
    total: number,
    extraParams?: Record<string, string | undefined>,
  ): TablePaginationConfig => ({
    current: page,
    pageSize,
    total,
    showSizeChanger: pageSizeOptions !== undefined,
    pageSizeOptions,
    itemRender: (targetPage, type, element) => {
      const lastPage = Math.ceil(total / pageSize);
      const disabled = (type === "prev" && page <= 1) || (type === "next" && page >= lastPage);
      if (disabled) return element;
      const children = isValidElement<{ children?: ReactNode }>(element) ? element.props.children : element;
      return <Link to={buildUrl(targetPage, pageSize, sortField, sortDirection, extraParams)}>{children}</Link>;
    },
  });

  const createTableChangeHandler =
    (extraParams?: Record<string, string | undefined>) =>
    (pagination: TablePaginationConfig, _filters: Record<string, unknown>, sorter: SorterInfo | SorterInfo[]) => {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextPageSize = pagination.pageSize ?? pageSize;
      const sizeChanged = nextPageSize !== pageSize;
      const nextPage = sizeChanged ? 1 : (pagination.current ?? 1);

      let nextSortField: string | undefined;
      let nextSortDirection: string | undefined;

      if (single?.order && typeof single.columnKey === "string" && single.columnKey in columnToSortFieldLookup) {
        nextSortField = columnToSortFieldLookup[single.columnKey];
        nextSortDirection = single.order === "ascend" ? SortDirection.Asc : SortDirection.Desc;
      }

      void navigate(buildUrl(nextPage, nextPageSize, nextSortField, nextSortDirection, extraParams));
    };

  return {
    offset: (page - 1) * pageSize,
    limit: pageSize,
    sortOrderFor,
    paginationConfig,
    createTableChangeHandler,
  };
};
